import { Check, ChevronDown, ChevronUp, Copy, Info, Link2, MailCheck, RefreshCw, Send, X } from 'lucide-react';
import { useState } from 'react';
import { SelectInput } from '@/components/Admin/AdminFormFields';
import { isStagingDb } from '@/config/config-select';
import { Button } from '@/components/ui/button';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { usePartnerInvites, usePartnerUsers } from '@/hooks/useInvites';
import {
  allowedRecipientDomainsLabel,
  isRecipientDomainAllowed,
  stagingAllowedRecipientDomains,
} from '@/types/types_email';
import {
  INVITE_DEFAULT_EXPIRY_DAYS,
  inviteStatusLabels,
  isInviteExpired,
  type InviteDeliveryType,
  type InviteType,
} from '@/types/types_invite';
import { partnerRoleLabels, type PartnerRoleType, type PartnerUserType } from '@/types/types_partnerUser';
import { callableErrorMessage, createPartnerInvite, revokePartnerInvite } from '@/util/callable_functions';
import { formatDate } from '@/util/util_format';
import { AccountActions, type AccountManageModeType } from './AccountActions';

const cardClass = 'tw:rounded-lg tw:border tw:bg-card tw:p-6';
const cardTitleClass =
  'tw:text-sm tw:font-semibold tw:tracking-wide tw:text-muted-foreground tw:uppercase';
const cardBlurbClass = 'tw:mt-1 tw:mb-5 tw:text-sm tw:text-muted-foreground';
const rowClass =
  'tw:flex tw:flex-wrap tw:items-center tw:justify-between tw:gap-x-4 tw:gap-y-2 tw:py-3 tw:first:pt-0 tw:last:pb-0';
const listClass = 'tw:divide-y tw:divide-ed-umber/10';
const metaClass = 'tw:mt-0.5 tw:truncate tw:text-xs tw:text-ed-stone';
const emptyClass = 'tw:py-2 tw:text-sm tw:text-ed-stone';
const errorClass = 'tw:mb-4 tw:text-sm tw:text-ed-red';
const noteClass = 'tw:mb-4 tw:flex tw:items-start tw:gap-1.5 tw:text-sm';

// Which addresses this deployment can actually email. Staging is restricted to
// our own domain (EMAIL_ALLOWED_RECIPIENT_DOMAINS, enforced in util_email.ts);
// production emails anyone. Said out loud in the form below, because an admin
// testing on staging otherwise learns it only from a failed send.
const emailableDomains = isStagingDb() ? stagingAllowedRecipientDomains : [];

const pastStatusPillClass: Record<'redeemed' | 'revoked', string> = {
  redeemed: 'tw:bg-green-100 tw:text-green-800',
  revoked: 'tw:bg-ed-umber/10 tw:text-ed-stone',
};

interface PartnerAccessProps {
  partnerId: string;
  partnerName: string;
  /**
   * 'admin' — the Eighty Days CMS view on the partner edit page: both roles,
   *           every account action, a configurable expiry.
   * 'owner'  — a partner manager managing their own team: staff only, and
   *            with defaults instead of knobs. Both are backed by the same
   *            callables, which enforce the difference server-side.
   */
  mode: AccountManageModeType;
  /** In 'owner' mode, the signed-in owner — so they can't act on themselves. */
  currentUid?: string;
}

// The manager first, then staff by name: the person who can change the
// partner's details is the one you are usually looking for.
function byRoleThenName(a: PartnerUserType, b: PartnerUserType): number {
  if (a.role !== b.role) return a.role === 'owner' ? -1 : 1;
  return (a.displayName || a.email).localeCompare(b.displayName || b.email);
}

/**
 * Who can sign in on one partner's behalf, and the invitations that would add
 * more — as two cards, not one.
 *
 * They were a single block and read as one list of people, which they are not:
 * an account is standing access, an invitation is a link that may never be
 * used. Splitting them means the top card answers "who can get in right now"
 * with nothing else in the way, and the bottom card is the only place that
 * grows as links come and go.
 *
 * Inviting has two doors. "Send invitation" is the one that should be taken:
 * the server emails the link, and it is never seen by anyone but the invitee.
 * "create a link instead" sits under it as a quiet second option — the same
 * invitation, handed over for the admin to pass on — because it is the answer
 * to a narrower question (a filtered domain, an address still being confirmed)
 * and it puts a live credential on someone's clipboard.
 *
 * A link returned by `createPartnerInvite` is the only copy that ever exists
 * outside the server — Firestore holds just its hash — so it is shown once in a
 * dialog. To re-send, use Resend or Regenerate, which revoke the old invitation
 * and issue a new one atomically.
 */
export function PartnerAccess({ partnerId, partnerName, mode, currentUid }: PartnerAccessProps) {
  const { invites, loading, error: listError } = usePartnerInvites(partnerId);
  const { partnerUsers } = usePartnerUsers(partnerId);

  const isAdminView = mode === 'admin';
  const who = partnerName || 'this partner';

  const [email, setEmail] = useState('');
  const [roleChoice, setRoleChoice] = useState<PartnerRoleType | null>(null);
  const [expiryDays, setExpiryDays] = useState(String(INVITE_DEFAULT_EXPIRY_DAYS));
  // Which button is waiting, not merely that one is. Every action on this card
  // shares `busy` to keep the others un-pressable, but the spinner belongs to the
  // one that was pressed — the two form buttons and one Resend per pending row
  // are otherwise indistinguishable. Keyed by the invite being re-issued where
  // there is one, and by the delivery method for a fresh invitation.
  const [running, setRunning] = useState<string | null>(null);
  const busy = running !== null;
  // One error per card: an account action failing has nothing to do with the
  // invitation form, and showing it there would send you looking in the wrong
  // place.
  const [accessError, setAccessError] = useState<string | null>(null);
  const [inviteError, setInviteError] = useState<string | null>(null);
  // An emailed invitation needs no dialog — the pending list below is the
  // receipt. A link does: it is shown once and then only its hash survives.
  const [sentTo, setSentTo] = useState<string | null>(null);
  const [newLink, setNewLink] = useState<{ url: string; email: string; sendError?: string } | null>(null);
  const [copied, setCopied] = useState(false);
  const [toRevoke, setToRevoke] = useState<InviteType | null>(null);
  const [showPast, setShowPast] = useState(false);

  // One manager per partner, enforced in redeemPartnerInvite. A partner
  // managing its own team can only ever create staff accounts.
  const existingOwner = partnerUsers.find((u) => u.role === 'owner');
  const pendingInvites = invites.filter((i) => i.status === 'pending');
  const pastInvites = invites.filter((i) => i.status !== 'pending');
  const hasPendingOwnerInvite = pendingInvites.some((i) => i.role === 'owner');
  const ownerSlotTaken = !!existingOwner || hasPendingOwnerInvite;

  // Default to the role that is actually available — manager for a partner with
  // none, staff once the role is taken — so the common case needs no choice.
  // Derived rather than initial state because the accounts arrive from a
  // subscription, after the first render. An explicit pick wins from then on.
  // Whether the address currently typed is one this deployment may email. The
  // server decides for real; this only warns, so a restriction the admin cannot
  // see coming does not turn into a link they were not expecting.
  const emailRestricted = emailableDomains.length > 0;
  const emailBlocked = emailRestricted && !!email.trim() && !isRecipientDomainAllowed(email, emailableDomains);

  const role = roleChoice ?? (ownerSlotTaken ? 'member' : 'owner');
  const inviteRole: PartnerRoleType = isAdminView ? role : 'member';
  const blocked = inviteRole === 'owner' && ownerSlotTaken;

  const accounts = [...partnerUsers].sort(byRoleThenName);

  async function issueInvite(
    inviteEmail: string,
    forRole: PartnerRoleType,
    delivery: InviteDeliveryType,
    replacesInviteId?: string,
  ) {
    setRunning(replacesInviteId ?? delivery);
    setInviteError(null);
    setSentTo(null);
    try {
      const res = await createPartnerInvite({
        partnerId,
        email: inviteEmail,
        role: forRole,
        expiryDays: Number(expiryDays) || INVITE_DEFAULT_EXPIRY_DAYS,
        replacesInviteId,
        delivery,
      });
      // `delivered` is what the server did, not what was asked for: an email it
      // could not send comes back as a link, with the reason attached.
      if (res.data.delivered === 'email') {
        setSentTo(res.data.email);
      } else {
        setNewLink({ url: res.data.url ?? '', email: res.data.email, sendError: res.data.sendError });
        setCopied(false);
      }
      setEmail('');
    } catch (e) {
      console.error('createPartnerInvite failed', e);
      setInviteError(callableErrorMessage(e));
    } finally {
      setRunning(null);
    }
  }

  async function confirmRevoke() {
    if (!toRevoke) return;
    setRunning('revoke');
    setInviteError(null);
    try {
      await revokePartnerInvite({ inviteId: toRevoke.id });
      setToRevoke(null);
    } catch (e) {
      console.error('revokePartnerInvite failed', e);
      setInviteError(callableErrorMessage(e));
    } finally {
      setRunning(null);
    }
  }

  async function copyLink() {
    if (!newLink) return;
    setRunning('copy');
    try {
      await navigator.clipboard.writeText(newLink.url);
      setCopied(true);
    } finally {
      setRunning(null);
    }
  }

  /** Whether this view may act on a given account. */
  function canManage(account: PartnerUserType): boolean {
    if (isAdminView) return true;
    // A manager manages staff, never another manager and never themselves —
    // the same rule `assertCanManagePartnerUser` applies.
    return account.role === 'member' && account.id !== currentUid;
  }

  return (
    <div className='tw:space-y-6'>
      {/* ---------- Who can sign in right now ---------- */}
      <section className={cardClass}>
        <h2 className={cardTitleClass}>{isAdminView ? 'Site Access' : 'Team'}</h2>
        <p className={cardBlurbClass}>
          {isAdminView
            ? `Everyone who can sign in on behalf of ${who}. A partner has one manager, who maintains the partner profile; staff can be added without limit.`
            : 'Everyone at your organisation who can sign in to Eighty Days. Only you can change the partner profile — staff see it read-only.'}
        </p>

        {accessError && <p className={errorClass}>{accessError}</p>}

        {accounts.length === 0 ? (
          <p className={emptyClass}>
            Nobody can sign in for {isAdminView ? who : 'your organisation'} yet — send an invitation below.
          </p>
        ) : (
          <ul className={listClass}>
            {accounts.map((u) => (
              <li key={u.id} className={rowClass}>
                <div className='tw:min-w-0'>
                  <div className='tw:truncate tw:text-sm tw:font-medium'>
                    {u.displayName}
                    {u.id === currentUid && <span className='tw:ml-2 tw:text-xs tw:text-ed-stone'>(you)</span>}
                    {u.status === 'disabled' && (
                      <span className='tw:ml-2 tw:rounded-full tw:bg-ed-umber/10 tw:px-2 tw:py-0.5 tw:text-xs tw:font-normal tw:text-ed-stone'>
                        Suspended
                      </span>
                    )}
                  </div>
                  <div className={metaClass}>
                    {partnerRoleLabels[u.role]} · {u.email}
                  </div>
                </div>
                {canManage(u) && (
                  <AccountActions
                    account={u}
                    mode={mode}
                    partnerName={partnerName}
                    hasOtherOwner={!!existingOwner && existingOwner.id !== u.id}
                    onError={setAccessError}
                  />
                )}
              </li>
            ))}
          </ul>
        )}
      </section>

      {/* ---------- Invitations ---------- */}
      <section className={cardClass}>
        <h2 className={cardTitleClass}>Invitations</h2>
        <p className={cardBlurbClass}>
          A single-use invitation that creates an account. It works only for the address you enter, and only
          once. We email it for you — or take the link and send it yourself.
        </p>

        <div className='tw:mb-4 tw:flex tw:flex-wrap tw:items-end tw:gap-3'>
          <div className='tw:min-w-56 tw:flex-1'>
            <Label className='tw:mb-1.5 tw:block'>Email address</Label>
            <Input
              type='email'
              value={email}
              placeholder='concierge@example.com'
              onChange={(e) => setEmail(e.target.value)}
            />
          </div>
          {/* Only Eighty Days chooses — a partner's own invitations are always
              staff, which `createPartnerInvite` enforces regardless. */}
          {isAdminView && (
            <>
              <div className='tw:w-40'>
                <Label className='tw:mb-1.5 tw:block'>Role</Label>
                <SelectInput
                  value={role}
                  options={partnerRoleLabels}
                  onValueChange={(v) => setRoleChoice(v as PartnerRoleType)}
                />
              </div>
              <div className='tw:w-28'>
                <Label className='tw:mb-1.5 tw:block'>Expires in</Label>
                <Input
                  type='number'
                  min={1}
                  max={90}
                  value={expiryDays}
                  onChange={(e) => setExpiryDays(e.target.value)}
                />
              </div>
            </>
          )}
          <Button
            type='button'
            disabled={busy || !email.trim() || blocked}
            loading={running === 'email'}
            onClick={() => void issueInvite(email.trim(), inviteRole, 'email')}
          >
            <Send /> {running === 'email' ? 'Working…' : 'Send invitation'}
          </Button>
          {/* The other door, kept deliberately quieter: same invitation, but the
              plaintext link ends up on a clipboard instead of only in an inbox.
              Worth having for a domain that filters us or an address being
              confirmed by phone — not worth offering as an equal choice. */}
          <span className='tw:pb-2 tw:text-sm tw:text-ed-stone'>or</span>
          <Button
            type='button'
            variant='ghost'
            disabled={busy || !email.trim() || blocked}
            loading={running === 'link'}
            title='Create the invitation without emailing it, and send the link yourself'
            onClick={() => void issueInvite(email.trim(), inviteRole, 'link')}
          >
            <Link2 /> Create a link
          </Button>
        </div>

        {/* Only ever shown where email is restricted, so production says
            nothing. Named as staging's doing rather than a fault of the
            address: the address is usually perfectly good, and the same
            invitation will be emailed for real from production. */}
        {emailRestricted && (
          <p className={`${noteClass} ${emailBlocked ? 'tw:text-amber-700' : 'tw:text-ed-stone'}`}>
            <Info className='tw:mt-0.5 tw:size-4 tw:shrink-0' />
            <span>
              {emailBlocked ? (
                <>
                  Staging cannot email {email.trim()} — it only sends to{' '}
                  {allowedRecipientDomainsLabel(emailableDomains)} addresses. Sending it anyway creates the
                  invitation and hands you the link; “Create a link” does the same without the detour.
                </>
              ) : (
                <>
                  Staging only emails {allowedRecipientDomainsLabel(emailableDomains)} addresses. An
                  invitation to anywhere else is created but not sent — you get the link to pass on instead.
                </>
              )}
            </span>
          </p>
        )}

        {/* Cleared by the next invitation, and by design not a dialog: there is
            nothing to copy and nothing to acknowledge. */}
        {sentTo && (
          <p className='tw:mb-4 tw:flex tw:items-center tw:gap-1.5 tw:text-sm tw:text-green-800'>
            <MailCheck className='tw:size-4' /> Invitation emailed to {sentTo}.
          </p>
        )}

        {/* Mirrors what createPartnerInvite enforces, so the reason a link can't
            be issued is visible before clicking. */}
        {blocked && (
          <p className='tw:mb-4 tw:text-sm tw:text-ed-stone'>
            {existingOwner
              ? `${who} already has a manager (${existingOwner.email}). Remove them, or change them to staff, to appoint a different one.`
              : 'A manager invitation is already outstanding. Revoke or regenerate it below.'}
          </p>
        )}

        {(inviteError || listError) && <p className={errorClass}>{inviteError ?? listError}</p>}

        <h3 className='tw:mb-1 tw:text-xs tw:font-medium tw:tracking-wider tw:text-ed-stone tw:uppercase'>
          Awaiting sign-up
        </h3>
        {loading ? (
          <p className={emptyClass}>Loading invitations…</p>
        ) : pendingInvites.length === 0 ? (
          <p className={emptyClass}>No invitations are outstanding.</p>
        ) : (
          <ul className={listClass}>
            {pendingInvites.map((invite) => {
              const expired = isInviteExpired(invite);
              return (
                <li key={invite.id} className={rowClass}>
                  <div className='tw:min-w-0'>
                    <div className='tw:truncate tw:text-sm tw:font-medium'>
                      {invite.email}
                      {expired && (
                        <span className='tw:ml-2 tw:rounded-full tw:bg-ed-umber/10 tw:px-2 tw:py-0.5 tw:text-xs tw:font-normal tw:text-ed-stone'>
                          Expired
                        </span>
                      )}
                    </div>
                    <div className={metaClass}>
                      {partnerRoleLabels[invite.role]} · {invite.delivery === 'email' ? 'emailed' : 'link'} ·{' '}
                      {expired ? 'expired' : 'expires'} {formatDate(invite.expiresAt)} · invited by{' '}
                      {invite.createdByEmail}
                    </div>
                    {/* The invitation is live either way — this only says that
                        nobody was told about it, which is the admin's to fix. */}
                    {invite.sendError && (
                      <div className='tw:mt-0.5 tw:text-xs tw:break-words tw:text-ed-red'>
                        Not emailed, so a link was created instead: {invite.sendError}
                      </div>
                    )}
                  </div>
                  <div className='tw:flex tw:shrink-0 tw:gap-1.5'>
                    {/* Re-issuing keeps the invite's own role and the way it was
                        delivered, not whatever the form above happens to be set
                        to. Either way the old link stops working. */}
                    <Button
                      type='button'
                      size='xs'
                      variant='outline'
                      disabled={busy}
                      loading={running === invite.id}
                      title={
                        invite.delivery === 'email'
                          ? 'Revoke this invitation and email a fresh one'
                          : 'Revoke this link and issue a new one'
                      }
                      onClick={() => void issueInvite(invite.email, invite.role, invite.delivery, invite.id)}
                    >
                      {invite.delivery === 'email' ? (
                        <>
                          <Send /> Resend
                        </>
                      ) : (
                        <>
                          <RefreshCw /> Regenerate
                        </>
                      )}
                    </Button>
                    <Button
                      type='button'
                      size='xs'
                      variant='destructive'
                      disabled={busy}
                      onClick={() => setToRevoke(invite)}
                    >
                      <X /> Revoke
                    </Button>
                  </div>
                </li>
              );
            })}
          </ul>
        )}

        {/* Redeemed and revoked links are a record, not something to act on, so
            they stay folded away rather than growing the list forever. */}
        {pastInvites.length > 0 && (
          <div className='tw:mt-5 tw:border-t tw:border-ed-umber/10 tw:pt-3'>
            <button
              type='button'
              onClick={() => setShowPast((v) => !v)}
              className='tw:flex tw:items-center tw:gap-1 tw:text-xs tw:text-ed-stone tw:transition-colors tw:hover:text-ed-gold'
            >
              {showPast ? <ChevronUp className='tw:size-3' /> : <ChevronDown className='tw:size-3' />}
              {showPast ? 'Hide' : 'Show'} {pastInvites.length} past invitation
              {pastInvites.length === 1 ? '' : 's'}
            </button>
            {showPast && (
              <ul className={`${listClass} tw:mt-3`}>
                {pastInvites.map((invite) => (
                  <li key={invite.id} className={rowClass}>
                    <div className='tw:min-w-0'>
                      <div className='tw:truncate tw:text-sm'>{invite.email}</div>
                      <div className={metaClass}>
                        {partnerRoleLabels[invite.role]} · invited by {invite.createdByEmail}
                      </div>
                    </div>
                    <span
                      className={`tw:shrink-0 tw:rounded-full tw:px-2 tw:py-1 tw:text-xs ${
                        pastStatusPillClass[invite.status as 'redeemed' | 'revoked']
                      }`}
                    >
                      {inviteStatusLabels[invite.status]}
                    </span>
                  </li>
                ))}
              </ul>
            )}
          </div>
        )}
      </section>

      {/* The one and only showing of the link — whether it was asked for, or is
          what came back when the email could not be sent. */}
      <Dialog open={!!newLink} onOpenChange={(open) => !open && setNewLink(null)}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Invitation link for {newLink?.email}</DialogTitle>
            <DialogDescription>
              Copy this now and send it to them — it is shown only once. If you lose it, use Regenerate to
              issue a fresh link.
            </DialogDescription>
          </DialogHeader>
          {newLink?.sendError && (
            <p className={errorClass}>
              The invitation was not emailed, so here is the link instead — it is valid and ready to send.
              Why: {newLink.sendError}
            </p>
          )}
          <div className='tw:rounded tw:border tw:border-ed-umber/20 tw:bg-ed-cream/40 tw:p-3 tw:font-mono tw:text-xs tw:break-all'>
            {newLink?.url}
          </div>
          <DialogFooter>
            <Button variant='outline' onClick={() => setNewLink(null)}>
              Done
            </Button>
            <Button loading={running === 'copy'} onClick={() => void copyLink()}>
              {copied ? <Check /> : <Copy />}
              {copied ? 'Copied' : 'Copy link'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      <Dialog open={!!toRevoke} onOpenChange={(open) => !open && !busy && setToRevoke(null)}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Revoke invitation?</DialogTitle>
            <DialogDescription>
              The link sent to {toRevoke?.email} will stop working immediately.
            </DialogDescription>
          </DialogHeader>
          <DialogFooter>
            <Button variant='outline' disabled={busy} onClick={() => setToRevoke(null)}>
              Cancel
            </Button>
            <Button variant='destructive' loading={running === 'revoke'} onClick={() => void confirmRevoke()}>
              Revoke
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
